import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, Legend, PieChart, Pie, Cell, Sector, Label, Tooltip } from 'recharts';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import { isEmpty, isEqual, sumBy } from 'lodash';
import {
  getActualTimeRange,
  chartGradientDefs,
  legendPayload,
  legendContent,
  getChartRenderRequirements,
  roundData,
  safeParseNumber,
  formatYAxisLabel,
  GROUP_BY_UNIQUE_COLORS,
  normalizePercentPieData,
  getThemeThenRandomSeriesColor,
  chartDotMenu
} from './utils'
import classNames from 'classnames';
import { ChartLoader } from './ChartLoader';
import ChartDotMenu from './ChartDotMenu';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants'
import { THEME_COLOR_SWATCH } from 'utils/constants'

class GaugeChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: []
      },
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      activeIndex: 0,
      isChartDataLoading: false,
      chartConfig: { ...CHART_DEFAULT_VALUES },
      hasMenu: false,
      maxPreviewRawValue: null,
      actualGaugeValue: null,
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  onDoughnutEnter = (_, index) => {
    this.setState({
      activeIndex: index,
    })
  }

  onDoughnutLeave = () => {
    this.setState({
      activeIndex: -1,
    })
  }

  onPieEnter = (_, index) => {
    this.setState({ activeIndex: index })
  }

  onPieLeave = () => {
    // Keep the current active slice highlighted when the cursor leaves
    this.setState(prev => ({ activeIndex: (typeof prev.activeIndex === 'number' && prev.activeIndex >= 0) ? prev.activeIndex : 0 }))
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      this.setState({ chartConfig: chartConfig })//, () => { console.log('After parsing and saving - this.state.chartConfig: ', this.state.chartConfig)})
    }

    if (this.props.isPreview) {
      this.setState({ chartConfig: this.props.chartValues, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
    // else {
    //   this.setData()
    // }
  }

  async componentDidUpdate(prevProps) {
    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    if (this.props.isPreview) {

      const currentChartValues = this.props.chartValues
      const prevChartValues = prevProps.chartValues

      if (!isEqual(this.props.chartType, prevProps.chartType)) {

        return this.setPreviewData()
      }

      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          const currentDataStreams = this.props.dataStreams
          const prevDataStreams = prevProps.dataStreams

          if (!isEqual(currentDataStreams, prevDataStreams)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.devices, prevChartValues.devices)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }


      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams

      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      if (this.props.refreshPreview) {
        return this.setState({
          chartConfig: this.props.chartValues
        })
      }
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      this.setState({ isChartDataLoading: false })
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  getChartDataValueSum = () => {
    const { chartData, chartConfig, actualGaugeValue } = this.state
    const rawValue = (typeof actualGaugeValue === 'number' && !Number.isNaN(actualGaugeValue))
      ? actualGaugeValue
      : sumBy(chartData.data, (entry) => Number(entry.value) || 0)
    const formatted = formatYAxisLabel(rawValue, chartConfig.format, chartConfig.decimal)
    const prefix = chartConfig?.prefix ? `${chartConfig.prefix} ` : ''
    const suffix = chartConfig?.suffix ? ` ${chartConfig.suffix}` : ''
    return `${prefix}${formatted}${suffix}`
  }

  renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180
    const { chartConfig } = this.state
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    } = props

    const fixedDecimal = chartConfig.decimal || 0

    // Removed old callout/label tooltip. Keep only the sector and a thin highlight ring.

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          cornerRadius={10}
          endAngle={endAngle}
          fill={fill}
          opacity={1.1}
        />
        {/* Thin outer highlight ring with rounded caps only on the outer shape */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 2}
          outerRadius={outerRadius + 4}
          cornerRadius={10}
          fill={fill}
          opacity={1.1}
        />
      </g>
    )
  }

  renderInactiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
    } = props

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          cornerRadius={10}
          fill={fill}
        />
        {/* Match active shape outer ring, so rounded caps are always visible */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          cornerRadius={10}
          innerRadius={outerRadius + 2}
          outerRadius={outerRadius + 4}
          fill={fill}
        />
      </g>
    )
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartType, nextProps.chartType) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading ||
      this.state.activeIndex !== nextState.activeIndex
    );
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, isNewVersion, chartValues, isDefaultTime } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    // Show loader immediately to avoid flashing error state
    this.setState({ isChartDataLoading: true })

    // Show loader immediately to avoid flashing error state
    this.setState({ isChartDataLoading: true })

    if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
      // Ensure every configured stream has at least one metric selected
      const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
      const anyMissingMetrics = !streams.every(s => Array.isArray(s?.metric?.metrics) && s.metric.metrics.length > 0)
      if (anyMissingMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }

    if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
      const hasMetrics = Array.isArray(chartConfig?.dataStreamSingle?.metric?.metrics) && chartConfig.dataStreamSingle.metric.metrics.length > 0
      if (!hasMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }



    // if (chartValues?.devices?.length === 0) {
    //   return this.setState({
    //     chartData: {
    //       groupBy: [],
    //       data: []
    //     },
    //     dataErrorMessage: "Error: No devices selected",
    //     validData: false
    //   })
    // }

    // if (groupByMetrics.length === 0) {
    //   return this.setState({
    //     chartData: {
    //       groupBy: [],
    //       data: []
    //     },
    //     dataErrorMessage: "Select a group by to display the chart",
    //     validData: false,
    //     isChartDataLoading: false
    //   })
    // }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false,
        isChartDataLoading: false
      })
    }

    let data = {}

    const devices = this.getDeviceList();

    if (this.props.isPreview) {
      if (this.props.dataStreams[0]) {
        this.setState({ isChartDataLoading: true })

        if (!devices.length) {
          return this.setState({ isChartDataLoading: false })
        }

        const response = await previewRawChartData({
          dataStreams: this.props.dataStreams,
          visoDevices: devices,
          dashboardId: this.props.dashboardId
        })
        if (response.data.errors) {
          this.setState({ isChartDataLoading: false })

          return defaultNotifier(response.data.errors)
        }

        data = {
          errors: response.data.errors || null,
          data: response.data.data.previewRawChartData
        }

        // Fetch MAX in background (store only, do not reflect in chart)
        try {
          const toMaxQuery = (q) => {
            if (!q || typeof q !== 'string') return q
            // Replace AVG( with MAX( conservatively
            return q.replace(/AVG\s*\(/gi, 'MAX(')
          }
          const maxStreams = Array.isArray(this.props.dataStreams)
            ? this.props.dataStreams.map(s => ({
              ...s,
              // Do NOT add unsupported fields; only change the SQL query
              query: toMaxQuery(s?.query)
            }))
            : []
          if (maxStreams.length > 0) {
            const maxResp = await previewRawChartData({
              dataStreams: maxStreams,
              visoDevices: devices,
              dashboardId: this.props.dashboardId
            })
            if (!maxResp.data.errors && Array.isArray(maxResp.data.data?.previewRawChartData)) {
              const first = maxResp.data.data.previewRawChartData[0]
              const fields = first?.result?.fields || []
              const candidates = fields.filter(k => k && k !== 'bucketed_time')
              const maxKey = candidates[0]
              const values = (first?.result?.data || []).map(r => Number(r?.[maxKey]) || 0)
              const maxValue = values.length ? Math.max(...values) : null
              this.setState({ maxPreviewRawValue: maxValue })
            } else {
              this.setState({ maxPreviewRawValue: null })
            }
          }
        } catch (e) {
          this.setState({ maxPreviewRawValue: null })
        }

        this.setState({ isChartDataLoading: false })
      }
      this.setState({
        dataErrorMessage: "Select a metric to display the preview"
      })
    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })

          if (response.errors) {
            const errorMessage = response.errors
            return this.setState({
              dataErrorMessage: errorMessage,
              validData: false,
              isChartDataLoading: false
            })
          }

          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })
          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        }

        if (data.errors) {
          const errorMessage = data.errors?.[0]?.message
          return this.setState({
            dataErrorMessage: errorMessage,
            validData: false,
            isChartDataLoading: false
          })
        }
      }
    }

    if (data.data) {
      const rawData = data.data.flatMap(item => item.result.data)
      const rawFields = data.data.flatMap(item => item.result.fields)

      const isSingleStream = Array.isArray(data.data) && data.data.length === 1
      if (isSingleStream && Array.isArray(rawData) && rawData.length > 0 && typeof this.state.maxPreviewRawValue === 'number' && this.state.maxPreviewRawValue > 0) {
        const fields0 = data.data[0]?.result?.fields || []
        const candidates = fields0.filter(k => k && k !== 'bucketed_time')
        const valueKey = candidates[0]
        const vals = (data.data[0]?.result?.data || []).map(r => Number(r?.[valueKey]) || 0)
        const val = vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length) : 0
        const percent = Math.max(0, Math.min(100, (val / this.state.maxPreviewRawValue) * 100))

        const colored = { name: 'value11', metric: 'value11', value: percent }
        const remainder = { name: 'remainder', metric: 'remainder', value: Math.max(0, 100 - percent), fill: '#eeeeee' }

        return this.setState({
          chartData: {
            groupBy: [],
            data: [colored, remainder],
            uniqueGroupValues: []
          },
          metricColumns: ['valuedddd'],
          dataErrorMessage: '',
          validData: true,
          isChartDataLoading: false,
          actualGaugeValue: val,
        })
      }

      let chartData = {};
      if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
        const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            const stream = streams[index] || {}
            const metricKey = (Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.length > 0)
              ? stream.metric.metrics.join(', ')
              : Object.keys(metric)[0]

            return {
              name: metricKey,
              metric: metricKey,
              value: Number(metric[rawFields[index]]) || 0,
              group: rawFields[index],
              index: index
            };
          })
        };
      }

      if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
        const categoryField = rawFields[0];
        const metricField = rawFields[1];
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            return {
              name: metric[categoryField], // name for legend/labels (e.g., "Claremont", "Rockingham")
              metric: metric[categoryField],
              value: Number(metric[metricField]) || 0,
              group: categoryField,
              index: index
            };
          })
        }
      };

      this.setState({
        chartData: {
          groupBy: chartData.groupBy,
          data: chartData.data,
          uniqueGroupValues: []
        },
        metricColumns: rawFields,
        dataErrorMessage: '',
        validData: true,
        isChartDataLoading: false,
        // cartesianConfig: mapChartValuesToCartesianConfig(this.state.chartConfig)
      })//, () => { console.log('After changes: ', this.state.cartesianConfig) })
    }
  }

  getOverrideData = (data) => {
    const { chartData, chartConfig } = this.state

    if (chartData.data.length === 0) {
      return ''
    }

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {

      const tiers = chartConfig.colorTiers.customConfigs;
      let labelText = tiers[0].text;
      let configData = tiers[0];

      for (const tier of tiers) {
        if (data > parseFloat(tier.value)) {
          labelText = tier.text;
          configData = tier;
        }
      }
      return configData

    }

    // const dataKey = Object.keys(chartData.data[0].values)[0]
    // const firstItem = chartData.data[0]
    // const values = firstItem?.values
    // const dataKey = values
    //   ? Object.keys(values)[0]
    //   : Object.keys(firstItem)[0]

    // const overrideValue = cardConfig?.dataOverrides[dataKey]?.value

    // if (overrideValue) {
    //   return overrideValue
    // }
    return null

  }


  getStrokeLine = (entry, index) => {
    const { chartConfig } = this.state

    let color = '';

    const configData = this.getOverrideData(entry.value);

    if (chartConfig?.dataStreams) {
      const key = entry?.metric || entry?.name || entry?.group || index
      const fallback = getThemeThenRandomSeriesColor({ key, index, palette: THEME_COLOR_SWATCH })
      const selectedColor = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
        ? (chartConfig.dataStreams.streams[index]?.streamLabel?.color || fallback)
        : fallback

      color = configData?.color || selectedColor
    }

    return color
  }

  getErrorMessage = () => {
    const { chartType, chartValues } = this.props;

    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    if ((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metric?.length) {
      return "Select a group by to display the chart"
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    const { chartType, chartValues } = this.props;

    if (!this.state.validData) {
      return true
    }

    if ((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'compare_streams') {
      return false;
    }

    if ((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metrics?.length) {
      return true;
    }

    return false
  }

  renderContent = () => {
    const { chart, height } = this.props
    const { chartData, validData, dataErrorMessage, chartConfig, dataWarnMessage } = this.state
    const formattedChartData = Array.isArray(chartData?.data) && chartData.data.map((d) => {
      return {
        ...d,
        bucketed_time: d.bucketed_time
      };
    });

    const baseRoundedData = roundData(formattedChartData, chartConfig.decimal);
    const processedChartData = (chartConfig.format === 'percent')
      ? normalizePercentPieData(baseRoundedData)
      : baseRoundedData;

    const isV2 = !!chartConfig && Object.prototype.hasOwnProperty.call(chartConfig, 'version')
    const titleText = (isV2 ? chartConfig?.chartTitle : (this.props.title || chartConfig?.chartTitle || this.props.chart?.title))
    // Determine breakdown type once; compute label per hovered slice inside tooltip
    const isCompareStreams = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
    const isByCategory = chartConfig?.dataStreamSingle?.breakdownType === 'by_category'

    // Calculate dynamic font sizes based on chart height
    const dataValueFontSize = Math.min(48, height * 0.12)
    const dataTitleFontSize = Math.min(18, height * 0.045)

    // Follow-cursor tooltip matching DoughnutChart
    const renderTooltipContent = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null
      const p = payload[0]
      const name = p?.payload?.metric || p?.name || p?.payload?.bucketed_time || ''
      // Hide tooltip for remainder slice entirely
      if (name === 'remainder') return null
      const isValueSlice = name === 'value'
      // Base raw value from slice; override for value slice with actualGaugeValue
      const baseRaw = Number(p?.value) || 0
      const actualVal = (typeof this.state.actualGaugeValue === 'number') ? this.state.actualGaugeValue : null
      const maxVal = (typeof this.state.maxPreviewRawValue === 'number') ? this.state.maxPreviewRawValue : null
      const raw = isValueSlice && actualVal !== null ? actualVal : baseRaw
      const idx = p?.payload?.index !== undefined ? p.payload.index : (Array.isArray(processedChartData)
        ? processedChartData.findIndex(d => d === p?.payload)
        : -1)

      // Compute stream label for the hovered slice index (idx)
      const streamLabelLocal = (idx >= 0)
        ? (
          isCompareStreams
            ? chartConfig?.dataStreamSingle?.dataStreams?.streams?.[idx]?.streamLabel?.value
            : chartConfig?.dataStreams?.streams?.[idx]?.streamLabel?.value
        )
        : ''

      // In by_category mode, legends use the category value itself as the label.
      // Mirror that here so hover labels exactly match legend labels.
      const displayLabel = isByCategory ? name : (streamLabelLocal || name)
      const total = Array.isArray(processedChartData)
        ? processedChartData.reduce((s, d) => s + (Number(d?.value) || 0), 0)
        : 0
      // Percentage for value slice should be computed vs max value
      const percentNum = (isValueSlice && maxVal && maxVal > 0)
        ? ((actualVal || 0) / maxVal) * 100
        : (total ? ((Number(baseRaw) || 0) / total) * 100 : 0)
      const swatchColor = (idx >= 0) ? this.getStrokeLine(processedChartData[idx], idx) : '#888'
      const formatted = `${chartConfig.prefix} ${formatYAxisLabel(raw, chartConfig.format, chartConfig.decimal)} ${chartConfig.suffix}`
      return (
        <div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: 8, padding: '6px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666', marginBottom: 2 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: swatchColor }} />
              <span>{displayLabel}</span>
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{formatted}</div>
          {total > 0 && <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{percentNum.toFixed(0)}%</div>}
        </div>
      )
    }

    return (
      <React.Fragment>
        {
          this.hasError()
            ?
            this.renderErrorContext()
            :
            <div className=''>
              <div className='grid-row'>
                <div className='width-100'>
                  <div className='grid-col'>
                    <div className='job-training__title job-training__title2'>{titleText}</div>
                  </div>
                </div>

                <div className='job-training__charts2 job-training__chart_container job-training__chart_container--gauge'>
                  <ResponsiveContainer width="100%" height={height}>
                    <PieChart margin={{ top: 10, right: 28, left: 20, bottom: 24 }} height={height}>
                      {processedChartData.map((entry, index) => {
                        // Skip gradient for remainder slice; it will be solid grey
                        if (entry?.metric === 'remainder' || entry?.name === 'remainder') {
                          return null
                        }
                        const fillId = `${this.props.chart.id}_${(entry?.metric || entry?.name || entry?.group)?.replaceAll(' ', '')}_${index}`
                        const stroke = this.getStrokeLine(entry, index)
                        console.log('stroke', stroke, fillId)
                        return (
                          <React.Fragment key={`fill-${index}`}>
                            {chartGradientDefs(fillId, stroke)}
                          </React.Fragment>
                        )
                      })}
                      <Pie
                        data={processedChartData}
                        activeIndex={this.state.activeIndex}
                        activeShape={this.renderActiveShape}
                        // inactiveShape={this.renderInactiveShape}
                        cx="50%"
                        cy="70%"
                        outerRadius='120%'
                        innerRadius='90%'
                        cornerRadius={0}
                        startAngle={196}
                        endAngle={-16}
                        paddingAngle={1}
                      >
                        {processedChartData.map((entry, index) => {
                          const isRemainder = (entry?.metric === 'remainder' || entry?.name === 'remainder')
                          const fillId = `${this.props.chart.id}_${(entry?.metric || entry?.name || entry?.group)?.replaceAll(' ', '')}_${index}`
                          const fill = isRemainder ? '#eeeeee' : `url(#${fillId}Fill)`

                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={fill}
                              cornerRadius={10}
                              className={`my-sector-class sector-${index}`}
                              onMouseEnter={isRemainder ? undefined : (_, i) => this.onPieEnter(_, index)}
                              onMouseLeave={isRemainder ? undefined : this.onPieLeave}
                              style={isRemainder ? { cursor: 'default', pointerEvents: 'none' } : {}}
                            />
                          )
                        })}

                        {/* <Cell fill="#eeeeee" /> */}
                        {chartConfig?.enableLabels &&
                          <Label
                            fill="#2184f9"
                            position="center"
                            value={this.getChartDataValueSum()}
                            className='charts-no-cartesian__gauge-label__data-value'
                            dy={0}
                            style={{ fontSize: `${dataValueFontSize}px`, fontWeight: 600 }}
                          />
                        }

                        {chartConfig.enableDataTitle &&
                          <Label
                            fill={(() => {
                              const entry = processedChartData[0];
                              if (!entry) return '#2184F9';
                              const overrideColor = chartConfig.dataOverrides ? chartConfig.dataOverrides[entry.metric]?.color : '';
                              const strokeColor = this.getStrokeLine(entry, 0);
                              return overrideColor || strokeColor || '#2184F9';
                            })()}
                            position="center"
                            value={chartConfig.dataTitle}
                            dy={Math.max(18, height * 0.055)}
                            className='charts-no-cartesian__gauge-label__data-title'
                            style={{ fontSize: `${dataTitleFontSize}px`, fontWeight: 500 }}
                          />
                        }
                      </Pie>

                      {/* Hover tooltip */}
                      <Tooltip
                        isAnimationActive={false}
                        wrapperStyle={{ pointerEvents: 'none', zIndex: 6 }}
                        cursor={{ fill: 'transparent' }}
                        content={renderTooltipContent}
                      />

                      {chartConfig.legend !== 'none' && (() => {
                        const basePayload = legendPayload(processedChartData, chartConfig.dataOverrides, chartConfig) || []
                        const coloredPayload = basePayload.map((entry, idx) => {
                          const original = entry?.payload || entry
                          const color = this.getStrokeLine(original, idx)
                          return { ...entry, color }
                        })

                        return (
                          <Legend
                            payload={coloredPayload}
                            verticalAlign={chartConfig.legend === 'right' ? 'middle' : 'bottom'}
                            align={chartConfig.legend === 'right' ? 'right' : 'center'}
                            layout={chartConfig.legend === 'right' ? 'vertical' : 'horizontal'}
                            iconType='square'
                            iconSize={14}
                            height={20}
                            chartHeight={height}
                            content={(props) => legendContent({
                              ...props,
                              payload: coloredPayload,
                              dataOverrides: chartConfig.dataOverrides,
                              chartConfig
                            })}
                            wrapperStyle={{
                              top: chartConfig.legend === 'right' && '50%',
                              transform: chartConfig.legend === 'right' && 'translateY(-50%)',
                              height: chartConfig.legend === 'right' && 'auto',
                              bottom: chartConfig.legend === 'right' ? 'auto' : '5px'
                            }}
                          />
                        )
                      })()}
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {dataWarnMessage && <div className='chart-error'>
                  {dataWarnMessage}
                </div>
                }
              </div>
            </div>
        }
      </React.Fragment>
    )
  }

  // Prevent unnecessary re-renders (e.g., from parent scroll)
  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartType, nextProps.chartType) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading
    );
  }

  render() {
    const { chart, isLegacy, color, isEditMode } = this.props;

    return (
      <Card
        noGrid
        color={this.props.color}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        chartId={!this.state.isChartDataLoading && (chart?.id || '66dece43216604577e3ab1a0')}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview chart-cards", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height + 85}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={!this.props.isPreview && this.props.onEdit && chartDotMenu({
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
      >
      </Card>
    );
  }
}

GaugeChart.propTypes = {
  onEdit: PropTypes.func,
  model: PropTypes.object,
  chart: PropTypes.any,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool
};

export default GaugeChart;